import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Table,
  Form,
  Badge,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import { FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

const AllocationSuggestionModal = ({ show, onHide, suggestion, onAllocated }) => {
  const [soItems, setSoItems] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [remainingQty, setRemainingQty] = useState(0);

  useEffect(() => {
    if (show && suggestion) {
      loadSOItems();
      setRemainingQty(suggestion.availableQty);
    }
  }, [show, suggestion]);

  const loadSOItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${config.API_BASE_URL}/allocation/suggestions/${suggestion._id}/so-items`
      );
      setSoItems(response.data.data);

      // Initialize allocations
      const initialAllocations = {};
      response.data.data.forEach(item => {
        initialAllocations[item.itemId] = 0;
      });
      setAllocations(initialAllocations);
      setError('');
    } catch (err) {
      setError('Failed to load sales orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAllocationChange = (itemId, value) => {
    const numValue = parseFloat(value) || 0;
    const item = soItems.find(i => i.itemId === itemId);

    if (!item) return;

    // Don't exceed pending qty for this item
    const maxForItem = item.pendingQty;
    const actualValue = Math.min(numValue, maxForItem);

    // Calculate total allocated
    const totalAllocated = Object.entries(allocations)
      .reduce((sum, [id, qty]) => {
        return sum + (id === itemId ? actualValue : qty);
      }, 0);

    // Don't exceed available qty
    if (totalAllocated <= suggestion.availableQty) {
      setAllocations(prev => ({
        ...prev,
        [itemId]: actualValue
      }));
      setRemainingQty(suggestion.availableQty - totalAllocated);
    } else {
      toast.warning('Cannot exceed available quantity');
    }
  };

  const handleAutoAllocate = () => {
    let remaining = suggestion.availableQty;
    const newAllocations = {};

    // Sort by delivery date (earliest first)
    const sortedItems = [...soItems].sort((a, b) =>
      new Date(a.deliveryDate) - new Date(b.deliveryDate)
    );

    sortedItems.forEach(item => {
      if (remaining > 0) {
        const allocate = Math.min(item.pendingQty, remaining);
        newAllocations[item.itemId] = allocate;
        remaining -= allocate;
      } else {
        newAllocations[item.itemId] = 0;
      }
    });

    setAllocations(newAllocations);
    setRemainingQty(remaining);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Format allocations for API
      const allocationList = Object.entries(allocations)
        .filter(([_, qty]) => qty > 0)
        .map(([itemId, qty]) => ({
          soItemId: itemId,
          qty: qty
        }));

      if (allocationList.length === 0) {
        toast.warning('Please allocate quantity to at least one order');
        return;
      }

      await axios.post(
        `${config.API_BASE_URL}/allocation/suggestions/${suggestion._id}/allocate`,
        { allocations: allocationList }
      );

      toast.success('Stock allocated successfully!');
      onAllocated();
      onHide();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to allocate quantities');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await axios.post(
        `${config.API_BASE_URL}/allocation/suggestions/${suggestion._id}/dismiss`
      );
      onHide();
      onAllocated();
    } catch (err) {
      console.error('Error dismissing suggestion:', err);
      onHide();
    }
  };

  if (!suggestion) return null;

  const totalAllocated = Object.values(allocations).reduce((sum, qty) => sum + qty, 0);

  return (
    <Modal show={show} onHide={onHide} size="xl" fullscreen='md' backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FiAlertCircle className="me-2" />
          Allocate Stock: {suggestion.productId?.productName || 'Product'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <div className="d-flex gap-2 flex-wrap justify-content-between align-items-center mb-3 p-3 bg-light rounded">
          <div>
            <strong>Available Quantity:</strong>{' '}
            <Badge bg="primary" className="fs-9">
              {suggestion.availableQty} {suggestion.productId?.unitId?.unitCode || 'units'}
            </Badge>
          </div>
          <div>
            <strong>Remaining:</strong>{' '}
            <Badge bg={remainingQty > 0 ? 'warning' : 'success'} className="fs-9">
              {remainingQty.toFixed(2)} {suggestion.productId?.unitId?.unitCode || 'units'}
            </Badge>
          </div>
          <div>
            <strong>Total Allocated:</strong>{' '}
            <Badge bg="info" className="fs-9">
              {totalAllocated.toFixed(2)} {suggestion.productId?.unitId?.unitCode || 'units'}
            </Badge>
          </div>
        </div>

        <div className="mb-3">
          <Button
            variant="outline-primary"
            onClick={handleAutoAllocate}
            disabled={loading}
          >
            <FiCheckCircle className="me-2" />
            Auto-Allocate by Delivery Date
          </Button>
        </div>

        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading orders...</p>
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead className="sticky-top bg-white">
                <tr>
                  <th>SO Number</th>
                  <th>Customer</th>
                  <th>Delivery Date</th>
                  <th className="text-end">Ordered</th>
                  <th className="text-end">Allocated</th>
                  <th className="text-end">Pending</th>
                  <th style={{ width: '150px' }}>Allocate Now</th>
                </tr>
              </thead>
              <tbody>
                {soItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No pending orders found for this product
                    </td>
                  </tr>
                ) : (
                  soItems.map((item) => (
                    <tr key={item.itemId}>
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>View Order</Tooltip>}
                        >
                          <span className="text-primary" style={{ cursor: 'pointer' }}>
                            {item.orderNumber}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td>{item.customerName}</td>
                      <td>
                        {item.deliveryDate
                          ? new Date(item.deliveryDate).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="text-end">{item.orderedQty} {item.unit}</td>
                      <td className="text-end">
                        <Badge bg="info">{item.allocatedQty} {item.unit}</Badge>
                      </td>
                      <td className="text-end">
                        <Badge bg="warning">{item.pendingQty} {item.unit}</Badge>
                      </td>
                      <td>
                        <div className="position-relative">
                        <Form.Control
                          type="number"
                          size="sm"
                          value={allocations[item.itemId] || 0}
                          onChange={(e) =>
                            handleAllocationChange(item.itemId, e.target.value)
                          }
                          min={0}
                          max={item.pendingQty}
                          step={0.01}
                        />
                          <span className="unit-suffix">{item.unit}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        <div className="mt-3 p-2 bg-light rounded">
          <small className="text-muted">
            <strong>Note:</strong> Allocations are prioritized by delivery date.
            Earlier dates are filled first.
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleDismiss} disabled={loading}>
          Dismiss
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || totalAllocated === 0}
        >
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Allocating...
            </>
          ) : (
            'Allocate Stock'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AllocationSuggestionModal;
